import { ICustomBinding } from "#/DefaultViewExtensionInterfaces";
import * as StringFormatter from '@/Utility/StringFormatter';
import RADApplication from '@/Management/RADApplication';
import Utilities from '@/Utility/Utilities';

type Timeouts = {
    timeout: number;
    closeTimeout: number;
}

export default class TooltipBinding implements ICustomBinding {

    /*
     * Verschiebungen des Tooltips gegenüber dem Mauszeiger
     */
    private static readonly staticOffsetLeft = 10;

    /*
     * Verschiebungen des Tooltips gegenüber dem Mauszeiger
     */
    private static readonly staticOffsetTop = 10;

    public initBinding() {
        let tooltipRemovers = [];
        let clearTooltips = () => {
            for (let i = 0; i < tooltipRemovers.length; i++) {
                tooltipRemovers[i]();
            }
            tooltipRemovers = [];
        };

        // Wenn irgendwo eine Maustaste gedrückt oder die Tastatur verwendet wird,
        // so schließen wir direkt alle Tooltips; dies erspart manuelles Schließen des Tooltips an vielen Stellen
        // (z.B. Klick auf einen Menüpunkt sollte auch das Tooltip verschwinden lassen etc.)
        $(document.body).on('mousedown keydown', clearTooltips);

        ko.bindingHandlers.tooltip = {
            update: (node: HTMLElement, valueAccessor: () => any) => {
                const GUI_TOOLTIP_DELAY = RADApplication.getInstance().getSystemParam('Gui.TooltipDelay', parseInt);
                const GUI_TOOLTIP_CLOSING_DELAY = RADApplication.getInstance().getSystemParam('Gui.TooltipClosingDelay', parseInt);
                let value = valueAccessor();

                let element = $(node);
                let tooltip: JQuery<HTMLElement>;
                
                let timeouts: Timeouts = { 
                    timeout: null, 
                    closeTimeout: null
                };
                
                let controlContainerElement = element.closest('.c-control');

                let delay = Utilities.isInt(GUI_TOOLTIP_DELAY) ? GUI_TOOLTIP_DELAY : 500;            // Verzögerung bis Tooltip angezeigt wird
                let closeTooltipDelay = Utilities.isInt(GUI_TOOLTIP_CLOSING_DELAY) ? GUI_TOOLTIP_CLOSING_DELAY : 5000;
                let moveTooltip = false;     // Soll der Tooltip mit dem Cursor mitbewegt werden?

                // Der Tooltip wird erst delay ms nach dem Hover angezeigt.
                // Bewegt man die Maus in der Zeit weiter, so sorgt der positionUpdater dafür,
                // das der Tooltip dann an der letzten bekannten Position angezeigt wird, statt an dem Punkt des Hover-Eintritts.
                // Falls moveTooltip=true ist und der Tooltip schon angezeigt wird, wird zudem direkt die reale Position des Tooltips angepasst.
                let positionUpdater = null;

                let text: string = null;
                if (typeof (value) === 'string') {
                    text = value;
                } else if (value && typeof value.caption === 'function') {
                    text = ko.unwrap(value.caption);

                    if (ko.isObservable(value.caption)) {
                        value.caption.subscribe(newCaption => {
                            text = newCaption;
                        });
                    }
                }

                let enableHtml = value?.htmlEnabled ?? false;

                if (text == null || text.trim() == '') {
                    // Einen leeren Text wollen wir nicht in einem Tooltip darstellen
                    if (element.data('destroyTooltip') != null) {
                        element.data('destroyTooltip')();
                    }
                    return;
                }

                if (element.data('reinitializeOpenedTooltip') != null) {
                    // Tooltip ist gerade offen und kann aktualisiert werden!
                    element.data('reinitializeOpenedTooltip')();
                    return;
                }
                else if (element.data('destroyTooltip') != null) {
                    // Wenn der Tooltip nicht geöffnet ist, aber schonmal initialisiert wurde,
                    // so müssen wir verhindern, dass die Hover-Callbacks doppelt registriert werden; dies verhindern wir hier,
                    // indem wir den Tooltip erstmal zerstören.
                    element.data('destroyTooltip')();
                }
                
                let clearTimeout = (timeouts: Timeouts) : void  => {
                    if (timeouts.timeout !== null) {
                        window.clearTimeout(timeouts.timeout);
                        timeouts.timeout = null;
                    }

                    if (timeouts.closeTimeout !== null) {
                        window.clearTimeout(timeouts.closeTimeout);
                        timeouts.closeTimeout = null;
                    }
                }

                let showTooltip = () => {
                    if (tooltip) {
                        clearTimeout(timeouts);

                        // Erst nach delay ms soll der Tooltip eingeblendet werden
                        timeouts.timeout = window.setTimeout(() => {
                            timeouts.timeout = null;
                            
                            tooltip.show();
                            timeouts.closeTimeout = window.setTimeout(() => {
                                timeouts.closeTimeout = null;
                                
                                hideTooltip();
                            }, closeTooltipDelay);
                        }, delay);
                    }
                }

                let hideTooltip = () => {
                    if (tooltip) {
                        clearTimeout(timeouts);
                        
                        tooltip.hide();
                    }
                }

                // Entfernt den hier gebundenen Tooltip
                let removeTooltip = () => {
                    clearTimeout(timeouts);

                    if (tooltip != null) {
                        tooltip.remove();
                        element.removeAttr('aria-describedby');
                        tooltip = null;
                    }

                    if (positionUpdater != null) {
                        element.off('mousemove', positionUpdater);
                        positionUpdater = null;
                    }

                    element.removeData('reinitializeOpenedTooltip');
                };

                // Beim Verlassen des zu hoverenden Elements mit der Maus, schließen wir auch das Tooltip wieder
                let onMouseLeave = () => {
                    // Titelattribut zurücksetzen
                    let title = element.data('title') as string;
                    element.attr('title', title);
                    element.removeData('title');

                    removeTooltip();
                    let idx = tooltipRemovers.indexOf(removeTooltip);
                    if (idx >= 0) {
                        tooltipRemovers.splice(idx, 1);
                    }
                };

                // Beim Hovern des Elementes mit der Maus lösen wir einen Timeout zur Anzeige des Tooltips aus
                let onMouseEnter = (event) => {

                    // Titelattribut löschen und zwischenspeichern, um browsereigenen Tooltip zu unterbinden
                    let title = element.attr('title');
                    element.data('title', title);
                    element.removeAttr('title');

                    clearTooltips(); // Alle bestehenden Tooltips schließen
                    tooltipRemovers.push(removeTooltip); // Neues Tooltip registrieren (es soll durch andere Tooltip-Aufrufe entfernt werden können)

                    let top, left;                    // Dynamisch aktualisierte Position des Tooltips
                    let width, height;                // Einmalig berechnte Breite und Höhe des Tooltips
                    let heightFromTop, widthFromLeft; // Einmalig berechnte Größen des Fensters, um Kollision/Overflow damit zu vermeiden

                    // Merkt sich bei Mausereignissen die aktuelle Position und wenn Tooltip-Bewegungen erwünscht ist und das Tooltip existiert
                    // so wird auch gleich die Position auf das Tooltip übertragen
                    positionUpdater = (event) => {
                        if (tooltip != null && tooltip.length > 0) {
                            top = event.pageY;
                            left = event.pageX;

                            TooltipBinding.positionTooltip(tooltip, top, left, height, width, heightFromTop, widthFromLeft);
                        }

                        // Wenn gerade der Invalidierungstext angezeigt wird, öffnen wir nicht den Tooltip
                        if (controlContainerElement.data('invalidation-text-shown')) {
                            hideTooltip();
                        } else {
                            showTooltip();
                        }
                    };

                    let initializeTooltip = () => {
                        if (moveTooltip && positionUpdater != null) {
                            element.on('mousemove', positionUpdater);
                        }

                        if (value != null && value.isDisposed) {
                            // Falls das ViewModel schon disposed ist, so schließen wir auch den Tooltip direkt
                            if (tooltip != null) {
                                tooltip.remove();
                            }
                            tooltip = null;
                            return;
                        }

                        // Falls der Observable-Text aktualisiert wird während der Tooltip geöffnet ist, so wird nur diese Methode aufgerufen
                        element.data('reinitializeOpenedTooltip', initializeTooltip);

                        let tooltipText = text || '';
                        if (!enableHtml) {
                            tooltipText = StringFormatter.escapeHtml(tooltipText, true);
                        }
                        tooltipText = StringFormatter.escapeWhitespacesAlternating(tooltipText);

                        let tooltipLines = tooltipText.split(/\r?\n/);
                        let rootTooltip = $('<div>');

                        jQuery.each(tooltipLines,
                            (i, line) => {
                                if (line === '')
                                    line = '&nbsp;';
                                let currentLine = $('<div>').html(line);
                                rootTooltip.append(currentLine);
                            });

                        // Tooltip-Element initialisieren
                        tooltip = $('body').children('.c-tooltip');
                        if (tooltip.length === 0) {
                            let tooltipId = _.uniqueId('tooltip');
                            tooltip = $(`<div id="${tooltipId}" class="c-tooltip">`).append(rootTooltip)
                                .css({ top: `${top}px`, left: `${left}px` })
                                .appendTo('body')
                                .hide();
                            element.attr('aria-describedby', tooltipId);
                            ko.applyBindingsToNode(tooltip[0], {
                                attr: {
                                    role: 'tooltip'
                                }
                            });
                        } else {
                            tooltip.empty();
                            tooltip.append(rootTooltip).css({ top: `${top}px`, left: `${left}px` }).show();
                        }

                        // Initiale Größenmessungen durchführen
                        let w = $(window);
                        heightFromTop = w.height() + w.scrollTop();
                        widthFromLeft = w.width() + w.scrollLeft();
                        width = tooltip.outerWidth();
                        height = tooltip.outerHeight();

                        // Tooltip erstmalig positionieren
                        TooltipBinding.positionTooltip(tooltip, top, left, height, width, heightFromTop, widthFromLeft);
                    };

                    initializeTooltip();

                    positionUpdater(event); // Initial die Position des Hover-Ereignisses merken
                };

                let destroyTooltip = () => {
                    if (element.data('destroyTooltip') == null) {
                        return;
                    }

                    onMouseLeave();
                    element.off('mouseenter', onMouseEnter).off('mouseleave', onMouseLeave);
                    controlContainerElement.off('invalidation-text-shown');

                    element.removeData('destroyTooltip');
                };

                element
                    .on('mouseenter', onMouseEnter)
                    .on('mouseleave', onMouseLeave);

                element.data('destroyTooltip', destroyTooltip);

                controlContainerElement.on('invalidation-text-shown', hideTooltip);

                ko.utils.domNodeDisposal.addDisposeCallback(node, destroyTooltip);
            }
        };
    }

    private static positionTooltip(tooltip: JQuery<HTMLElement>, top, left, height, width, heightFromTop, widthFromLeft) {
        if (top + height + TooltipBinding.staticOffsetTop >= heightFromTop) {
            top = top - height - TooltipBinding.staticOffsetTop;
        } else
            top = top + TooltipBinding.staticOffsetTop;

        // Wenn Tooltip zu breit
        if (left + width + TooltipBinding.staticOffsetLeft >= widthFromLeft) {
            // Nach links versuchen
            if (left - width - TooltipBinding.staticOffsetLeft >= TooltipBinding.staticOffsetLeft) {
                left = left - width - TooltipBinding.staticOffsetLeft;
            } else {
                // Wenn nicht passt, dann gucken, wo mehr Platz
                if (left < widthFromLeft / 2) {
                    left = left + TooltipBinding.staticOffsetLeft;
                    width = widthFromLeft - left - TooltipBinding.staticOffsetLeft;
                } else {
                    width = left - TooltipBinding.staticOffsetLeft;
                    left = TooltipBinding.staticOffsetLeft;
                }
            }
        } else {
            left = left + TooltipBinding.staticOffsetLeft;
        }

        tooltip.css({
            top: top,
            left: left,
            width: width
        });
    }
}